import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { apiClientWithApi, apiLoaderClientWithApi } from '../../utils/http';
import {
  extractBlobError,
  resolveXlsxFileName,
  triggerBlobDownload,
} from '../../utils/http/responseHelpers';
import type {
  TaskFormsReportRequest,
  UploadResult,
  UploadTasksPayload,
} from './TaskFormState';
import { repeatSerializer } from './TaskFormQuery';
export const getTaskFormReport = createAsyncThunk<
  string,
  { request: TaskFormsReportRequest; campaignName?: string },
  { rejectValue: string }
>(
  'taskForm/getReport',
  async ({ request, campaignName }, { rejectWithValue }) => {
    try {
      const response = await apiClientWithApi.get('task/form/report', {
        params: request,
        paramsSerializer: repeatSerializer,
        responseType: 'blob',
      });

      const safeCampaign = campaignName?.trim().replace(/[\\/:*?"<>|]/g, '_');
      const fallbackFileName = safeCampaign
        ? `journal_survey_${safeCampaign}.xlsx`
        : 'journal_survey.xlsx';
      const fileName = resolveXlsxFileName(
        response.headers['content-disposition'],
        fallbackFileName,
      );

      triggerBlobDownload(new Blob([response.data]), fileName);
      return fileName;
    } catch (error) {
      // `responseType: 'blob'` wraps JSON error bodies in a Blob — unpack them
      // so the modal can surface the real backend message.
      const message = await extractBlobError(
        error,
        'Не удалось сформировать отчет!',
      );
      return rejectWithValue(message);
    }
  },
);

export const uploadTaskForms = createAsyncThunk<
  UploadResult,
  UploadTasksPayload
>('taskForm/uploadTasks', async (payload, { rejectWithValue }) => {
  try {
    const params: Record<string, unknown> = {
      type: payload.type,
      from: payload.from,
    };
    if (payload.name) params.name = payload.name;
    if (payload.templateId) params.templateId = payload.templateId;

    const response = await apiLoaderClientWithApi.post<UploadResult>(
      'task/form/load/file',
      payload.file,
      {
        params,
        paramsSerializer: repeatSerializer,
      },
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return rejectWithValue(error.response?.data ?? error.message);
    }
    return rejectWithValue('Не удалось загрузить задания!');
  }
});

export const downloadTaskFormTemplate = createAsyncThunk<
  string,
  void,
  { rejectValue: string }
>('taskForm/downloadTemplate', async (_, { rejectWithValue }) => {
  try {
    const response = await apiLoaderClientWithApi.get(
      'task/form/load/file/template',
      { responseType: 'blob' },
    );

    const fileName = resolveXlsxFileName(
      response.headers['content-disposition'],
      'Шаблон анкетирование.xlsx',
    );

    triggerBlobDownload(new Blob([response.data]), fileName);
    return fileName;
  } catch (error) {
    const message = await extractBlobError(error, 'Не удалось скачать шаблон!');
    return rejectWithValue(message);
  }
});
